import { Play as PlayIcon } from '@tunasong/icons'
import type { Chord, ChordMatch, ChordVariant, NoteEvent } from '@tunasong/music-lib'
import {
  ChordLib,
  NoteLib,
  analyzeChord,
  computeHarmonicTension,
  detectModalSignature,
  getChordFunctionAriaLabel,
  isChordVariant,
  isNumeralChordFunction,
  nextChord,
} from '@tunasong/music-lib'
import type { ChordBorrowing } from '@tunasong/models'
import type { NoteName, Scale } from '@tunasong/schemas'
import { Chip, Divider, IconButton, Tooltip, Typography } from '@tunasong/ui-lib'
import cn from 'classnames'
import type { FC, ReactNode } from 'react'
import { GuitarChord } from '../guitar/visualization/guitar-chord.js'
import { usePlayNotes } from '../player/piano-player.hook.js'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { getChordConfidenceClassName, shouldRenderChordConfidenceLabel } from './chord-confidence.js'
import styles from './chord-info-popup.module.css'

export interface ChordInfoPopupProps {
  chord: Chord
  scale: Scale | null
  onInsertSuggestion: (chord: ChordVariant) => void
  onClose: () => void
  /** Extra actions (e.g. block actions) rendered at the bottom of the card, on its surface */
  actions?: ReactNode
  /** The cover-song "Worth a second listen" section, shown above the analysis rows (chord-check UX §3) */
  checkSection?: ReactNode
}

const NOTE_SEPARATOR = ' – ' // en-dash with regular spaces

const chipColorFor = (borrowing: ChordBorrowing): 'info' | 'default' => {
  // parallel-mode → default (Bowie 2026-05-09): a `warning` tint would imply
  // "songwriting error" for what is actually an expressive choice. The `♭`
  // accidental + description line already convey "borrowed".
  if (borrowing === 'secondary-dominant') {
    return 'info'
  }
  return 'default'
}

export const ChordInfoPopup: FC<ChordInfoPopupProps> = props => {
  const { chord, scale, actions, checkSection } = props
  const { playNotes } = usePlayNotes()

  const chordName = ChordLib.getName(chord)
  const notes = isChordVariant(chord) ? ChordLib.getNotes(chord) : []
  const prettyNotes = notes.map(n => NoteLib.prettifyNote(n))
  const analysis = isChordVariant(chord) && scale ? analyzeChord({ chord, scale }) : null

  // S3a (Phase-1 theory cues): emit a level-gated "modal characteristic" line
  // when the chord carries its mode's signature note (e.g., G major in D Dorian
  // contains B-natural = ♮6). Only fires in the four supported modes
  // (Dorian/Mixolydian/Phrygian/Lydian) — parent-scale keys produce no modal
  // hits. Computed unconditionally for hook-order stability; empty id resolves
  // to null (no row).
  const modalHit = isChordVariant(chord) && scale ? (detectModalSignature([chord], scale)[0] ?? null) : null
  const modalCopy = useTheoryCopy(modalHit?.lexiconId ?? '')

  // Phase 1.5 (analyzer-lexicon consolidation): the rationale row resolves
  // through `useTheoryCopy(functionLexiconId)` and absorbs both diatonic
  // (`function-*`) and parallel-mode-borrowing (`borrowed-*`) lexicon entries.
  // The separate S3b borrowed-line row was retired here — the rationale row
  // already carries the borrowed copy directly because the analyzer emits
  // `borrowed-*` for parallel-mode borrowings (Bowie 2026-05-09). Falls back
  // to analysis.description when the id is null (chromatic / V/X / modal-
  // distinctive / tritone-sub) or when theoryLevel === 'off'. Hook call is
  // unconditional for hook-order stability; empty id resolves to null.
  const functionCopy = useTheoryCopy(analysis?.functionLexiconId ?? '')

  // Track A — Tension Contour Stripe reveal row. Reuse the popup's existing
  // `analysis` (no extra analyzeChord) to resolve the chord's tension bucket,
  // then render that bucket's lexicon copy through the same useTheoryCopy path
  // as the rationale row. Hook call is unconditional for hook-order stability;
  // empty id (no analysis) resolves to null → no row. `theoryLevel === 'off'`
  // resolves to null inside useTheoryCopy.
  const tension = analysis ? computeHarmonicTension({ chord, analysis, scale: scale ?? undefined }) : null
  const tensionCopy = useTheoryCopy(tension ? `tension-${tension.bucket}` : '')

  const handlePlay = () => {
    if (!isChordVariant(chord)) {
      return
    }
    const events: NoteEvent[] = ChordLib.getNotes(chord).map(n => ({
      type: 'note',
      note: `${n}4` as NoteName,
      duration: '4n',
    }))
    playNotes(events)
  }

  // Confidence gating: hide the chip and description entirely on `low` (no
  // placeholder), apply 70% opacity on `medium`, render as-is on `high`. The
  // chord NAME above is always shown regardless of confidence (Bowie invariant).
  // Chromatic chords always show their rationale ("Outside the key.") — it's
  // definitive feedback, not a low-confidence label.
  const isChromatic = analysis !== null && analysis.chordFunction === 'chromatic'
  const showRomanChip =
    analysis !== null &&
    isNumeralChordFunction(analysis.chordFunction) &&
    shouldRenderChordConfidenceLabel(analysis.confidence)
  const showRationale = analysis !== null && (isChromatic || shouldRenderChordConfidenceLabel(analysis.confidence))
  const confidenceClassName = analysis ? getChordConfidenceClassName(analysis.confidence) : undefined
  const rationaleClassName = isChromatic ? undefined : confidenceClassName
  const ariaLabel = analysis ? getChordFunctionAriaLabel(analysis.chordFunction, scale) : null

  // Tier 4: up to 2 suggested follow-on chords. ChordLib.equals returns
  // boolean | undefined; treat undefined as "not equal" so we don't drop
  // valid suggestions.
  const suggestions: ChordMatch[] =
    isChordVariant(chord) && scale
      ? nextChord({ current: chord, scale })
          .filter(m => ChordLib.equals(m.chord, chord) !== true)
          .slice(0, 2)
      : []

  return (
    <div className={styles.card} role="dialog" aria-label={chordName}>
      <div className={styles.identityRow}>
        <h2 className={styles.identityName}>{chordName}</h2>
        {showRomanChip && analysis && ariaLabel ? (
          <Tooltip title={ariaLabel}>
            <Chip
              size="small"
              color={chipColorFor(analysis.borrowing)}
              aria-label={ariaLabel}
              label={analysis.chordFunction}
              tabIndex={-1}
              className={confidenceClassName}
            />
          </Tooltip>
        ) : null}
        <Tooltip title="Play chord">
          <IconButton aria-label="Play chord" onClick={handlePlay} size="small">
            <PlayIcon />
          </IconButton>
        </Tooltip>
      </div>
      {checkSection}
      {prettyNotes.length > 0 ? (
        <div className={styles.notes} aria-label={`Notes: ${prettyNotes.join(', ')}`}>
          {prettyNotes.join(NOTE_SEPARATOR)}
        </div>
      ) : null}
      {showRationale && analysis ? (
        <div className={cn(styles.rationale, rationaleClassName)}>
          {functionCopy ? functionCopy.body : analysis.description}
        </div>
      ) : null}
      {modalHit && modalCopy ? <div className={cn(styles.rationale, styles.modalLine)}>{modalCopy.body}</div> : null}
      {tension && tensionCopy ? (
        <div className={cn(styles.rationale, styles.tensionLine)} data-tension-row data-tension-bucket={tension.bucket}>
          {tensionCopy.body}
        </div>
      ) : null}
      <Divider />
      <div className={styles.body}>
        <div className={styles.diagramSlot}>
          <GuitarChord chord={chord} scale={scale ?? undefined} />
        </div>
        {suggestions.length > 0 ? (
          <div className={styles.suggestions}>
            <div className={styles.suggestionsHeading}>Goes well with</div>
            <div className={styles.suggestionsRow}>
              {suggestions.map(match => {
                const suggestionAnalysis = scale ? analyzeChord({ chord: match.chord, scale }) : null
                const showFnLabel =
                  suggestionAnalysis !== null &&
                  isNumeralChordFunction(suggestionAnalysis.chordFunction) &&
                  shouldRenderChordConfidenceLabel(suggestionAnalysis.confidence)
                const fnLabel = showFnLabel && suggestionAnalysis ? suggestionAnalysis.chordFunction : ''
                const fnClassName = suggestionAnalysis
                  ? getChordConfidenceClassName(suggestionAnalysis.confidence)
                  : undefined
                const name = ChordLib.getName(match.chord)
                return (
                  <button
                    key={`${match.chord.root}-${match.chord.variant}`}
                    type="button"
                    className={styles.suggestionChip}
                    aria-label={`Insert ${name}${fnLabel ? ` (${fnLabel})` : ''}`}
                    onClick={() => props.onInsertSuggestion(match.chord)}
                  >
                    <span className={styles.suggestionChipName}>{name}</span>
                    {fnLabel ? <span className={cn(styles.suggestionChipFunction, fnClassName)}>{fnLabel}</span> : null}
                  </button>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
      {scale === null ? (
        <Typography variant="body2" className={styles.footerNote}>
          Set a key for chord analysis
        </Typography>
      ) : null}
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  )
}

export default ChordInfoPopup
